let paddleLoaderPromise = null;
let paddleInitialized = false;

const PADDLE_SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

function getClientToken() {
  return String(process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '').trim();
}

function getEnvMode() {
  const mode = String(process.env.REACT_APP_PADDLE_ENV || 'sandbox').trim().toLowerCase();
  return mode === 'live' ? 'live' : 'sandbox';
}

export function getPriceIdForPlan(planCode) {
  const code = String(planCode || '').toLowerCase();
  if (code === 'starter') return String(process.env.REACT_APP_PADDLE_PRICE_STARTER || '').trim();
  if (code === 'premium') return String(process.env.REACT_APP_PADDLE_PRICE_PREMIUM || '').trim();
  return '';
}

export function canUsePaddleCheckout(planCode) {
  return Boolean(getClientToken() && getPriceIdForPlan(planCode));
}

function loadPaddleScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window is not available'));
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (paddleLoaderPromise) return paddleLoaderPromise;

  paddleLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (!window.Paddle) {
        reject(new Error('Paddle SDK loaded but Paddle is unavailable'));
        return;
      }
      resolve(window.Paddle);
    };
    script.onerror = () => reject(new Error('Failed to load Paddle SDK'));
    document.head.appendChild(script);
  });

  return paddleLoaderPromise;
}

async function ensurePaddleInitialized({ userId, userEmail }) {
  const token = getClientToken();
  if (!token) throw new Error('Paddle client token is missing');

  const Paddle = await loadPaddleScript();

  if (!paddleInitialized) {
    if (getEnvMode() === 'sandbox' && Paddle.Environment?.set) {
      Paddle.Environment.set('sandbox');
    }

    // pwCustomer accepts Paddle customer id (ctm_...) or email.
    // Supabase UUID is not a valid Paddle customer id, so we pass only email.
    const pwCustomer = userEmail ? { email: userEmail } : undefined;

    Paddle.Initialize({
      token,
      ...(pwCustomer ? { pwCustomer } : {})
    });
    paddleInitialized = true;
  }

  return Paddle;
}

export async function openPlanCheckout({ planCode, userId, userEmail, language = 'ro' }) {
  const priceId = getPriceIdForPlan(planCode);
  if (!priceId) throw new Error(`Missing Paddle price id for plan: ${planCode}`);
  if (!/^pri_/i.test(priceId)) {
    throw new Error(
      `Invalid Paddle price id for plan "${planCode}". Expected a Price ID (pri_...), got "${priceId}".`
    );
  }
  if (!userId) throw new Error('Missing authenticated user id');

  const Paddle = await ensurePaddleInitialized({ userId, userEmail });
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://chessmindset.academy';
  const successUrl = `${appOrigin}/#/billing?checkout=success`;
  const cancelUrl = `${appOrigin}/#/billing?checkout=cancel`;

  Paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customData: {
      user_id: userId,
      supabase_user_id: userId,
      plan_code: String(planCode || '').toLowerCase()
    },
    ...(userEmail ? { customer: { email: userEmail } } : {}),
    successUrl,
    cancelUrl,
    settings: {
      displayMode: 'overlay',
      locale: language === 'ro' ? 'ro' : 'en',
      successUrl
    }
  });
}
